import { HttpErrorResponse } from '@angular/common/http'
import {
  INVALID_FIELD_TYPE,
  InvalidFieldTypeBody,
} from '../models/transactions-query.model'

/**
 * How a /query call failed, in the shapes the UI has to present DIFFERENTLY. Anything the user cannot
 * act on differently is deliberately collapsed into `'unknown'`.
 *
 * Ported from `libs/panels/positions-2/src/lib/services/query-error.ts` (PVAL-26129 §6).
 *
 * **flex-transactions does not send the 400 yet.** Both `FlexTransactionQueryService` and
 * `FlexExternalTransactionQueryService` let the Postgres cast error through, so a chosen type the data
 * contradicts currently arrives as a 500 and is reported by the grid's error toast, over an empty
 * table. The classifier and the Reset-to-Text path are built now
 * so the behaviour lights up the moment the backend ports `asFieldTypeError`; that port is raised as a
 * follow-up with the PVAL-26445 / PVAL-26340 owner and does not block this ticket.
 */
export type QueryFailureKind =
  /**
   * 400 INVALID_FIELD_TYPE — a chosen comparison type Postgres could not apply to a value. Actionable
   * and per-column: it names the offending value and every field that claimed the failing type, and
   * the fix is to reset those columns to Text.
   */
  | 'invalid-field-type'
  /**
   * 410 Gone — the `helios-session-id` is missing or expired. NOT a 5xx: nothing is broken and
   * retrying the same request cannot help, so it must not be presented as one (and must not be
   * presented as a zero-row result either, which is what an unhandled failure degrades into).
   */
  | 'session-expired'
  /** Everything else: a transient failure worth a Retry. */
  | 'unknown'

export interface QueryFailure {
  kind: QueryFailureKind
  /**
   * Every field the backend named. A LIST, not one column: Postgres reports the offending value and
   * not the column it came from, so both fields claiming the failing type are named — and all of them
   * have to be reset together, or the retry fails identically.
   */
  fields: string[]
  /** The type that could not be applied, e.g. `number`. */
  requestedType?: string
  /** The value that could not be read, rendered for display. The known trigger is an empty string. */
  value?: string
  /** The Helios ReportableError envelope's id, so a bug report can be keyed on it. */
  errorId?: string
  /** The backend's own message, when it sent one. */
  message?: string
}

/** The Helios ReportableError (StandardApiError) envelope, of which only `errorId` is needed here. */
interface ReportableErrorEnvelope {
  errorId?: string
}

/**
 * Render the offending value for display.
 *
 * The known trigger is Prometheus sending an EMPTY STRING for a key that does not apply to an
 * instrument, so the interesting case is precisely the one that renders as nothing. `(empty)` keeps
 * the message readable — `the value  could not be read as a number` reads like a bug in the message
 * itself.
 */
const displayValue = (value: unknown): string | undefined => {
  if (value === undefined) return undefined
  if (value === null) return '(none)'
  const text = String(value)
  return text.trim() === '' ? '(empty)' : text
}

/**
 * Classify a failed /query response.
 *
 * Total and never-throwing: a transport-level failure with no body, a 400 that is NOT
 * INVALID_FIELD_TYPE, and a genuine 500 all land on `'unknown'` and are offered a Retry — the two
 * specific kinds are only claimed when the response actually evidences them.
 */
export function classifyQueryError(err: unknown): QueryFailure {
  if (!(err instanceof HttpErrorResponse))
    return { kind: 'unknown', fields: [] }

  const envelope = err.error as
    | (InvalidFieldTypeBody & ReportableErrorEnvelope)
    | null
    | undefined
  const errorId = envelope?.errorId

  if (err.status === 410) {
    return { kind: 'session-expired', fields: [], errorId }
  }

  if (err.status === 400 && envelope?.error === INVALID_FIELD_TYPE) {
    return {
      kind: 'invalid-field-type',
      // Defensive: the contract says `fields` is always a list, but attributing to nothing is better
      // than attributing to a string that got iterated character by character.
      fields: Array.isArray(envelope.fields) ? [...envelope.fields] : [],
      requestedType: envelope.requestedType,
      value: displayValue(envelope.value),
      message: envelope.message,
      errorId,
    }
  }

  return { kind: 'unknown', fields: [], errorId }
}

/**
 * The user-facing sentence for an INVALID_FIELD_TYPE failure, naming the column(s) and the value.
 *
 * Prefers the backend's own `message` when it sent one — it already names the column and value in a
 * single sentence, and reconstructing an equivalent one here risks the two silently saying slightly
 * different things over time. The fields/requestedType/value reconstruction is the fallback for a
 * response that carries the structured fields but not the sentence.
 */
export function invalidFieldTypeMessage(failure: QueryFailure): string {
  if (failure.message) return failure.message

  const columns = failure.fields.length
    ? failure.fields.join(', ')
    : 'a filtered column'
  const asType = failure.requestedType ? ` as ${failure.requestedType}` : ''
  const value =
    failure.value === undefined
      ? ''
      : ` The value ${failure.value} could not be read${asType}.`
  return `${columns} can't be compared${asType}.${value}`
}
